import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Calendar, Phone, Mail, Sparkles, ThumbsUp, ThumbsDown, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useSEO, SEO_PRESETS } from '@/hooks/useSEO';
import { supabase } from '@/lib/supabase';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: string;
  needsContact?: boolean;
  needsAppointment?: boolean;
}

interface LeadForm {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const Chat = () => {
  // SEO für Chat-Seite
  useSEO(SEO_PRESETS.chat);
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId] = useState(() => crypto.randomUUID());
  const [loading, setLoading] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showAppointmentPrompt, setShowAppointmentPrompt] = useState(false);
  const [leadForm, setLeadForm] = useState<LeadForm>({ name: '', email: '', phone: '', message: '' });
  const [feedbackGiven, setFeedbackGiven] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Quick response buttons
  const quickResponses = [
    { label: "💻 Computer langsam", message: "Mein Computer ist sehr langsam geworden. Was kann ich tun?" },
    { label: "📧 E-Mail Problem", message: "Ich habe Probleme mit meinen E-Mails. Können Sie mir helfen?" },
    { label: "🌐 WLAN funktioniert nicht", message: "Mein WLAN funktioniert nicht. Was soll ich machen?" },
    { label: "📱 Smartphone Hilfe", message: "Ich brauche Hilfe mit meinem Smartphone." },
    { label: "💰 Was kostet das?", message: "Was kostet eine Stunde IT-Support bei Ihnen?" },
    { label: "🕐 Wann sind Sie erreichbar?", message: "Wann sind Sie erreichbar und wie kann ich einen Termin buchen?" },
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize with welcome message
  useEffect(() => {
    const welcomeMessage: Message = {
      id: 'welcome',
      content: 'Hallo! 👋 Ich bin der KI-Assistent von Klartech Müritz. Wie kann ich Ihnen heute bei Ihrem IT-Problem helfen?',
      role: 'assistant',
      timestamp: new Date().toISOString()
    };
    setMessages([welcomeMessage]);
  }, []);

  const trackConversation = async (userMessage: string, botResponse: string, category?: string, escalated?: boolean, appointment?: boolean) => {
    try {
      await supabase.from('chat_conversations').insert({
        session_id: sessionId,
        user_message: userMessage,
        bot_response: botResponse,
        category: category,
        escalated_to_contact: escalated || false,
        appointment_requested: appointment || false
      });
    } catch (error) {
      console.error('Error tracking conversation:', error);
    }
  };

  const sendMessage = async (messageText?: string) => {
    const text = messageText || inputMessage;
    if (!text.trim() || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: text,
      role: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      // Build message history for context
      const messageHistory = messages
        .filter(m => m.id !== 'welcome')
        .map(m => ({ role: m.role, content: m.content }));
      
      messageHistory.push({ role: 'user', content: text });

      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: { messages: messageHistory }
      });

      if (error) throw error;

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: data.message,
        role: 'assistant',
        timestamp: new Date().toISOString(),
        needsContact: data.needsContact,
        needsAppointment: data.needsAppointment
      };

      setMessages(prev => [...prev, aiMessage]);

      // Track conversation
      await trackConversation(text, data.message, undefined, data.needsContact, data.needsAppointment);

      // Show appropriate prompts based on AI response
      if (data.needsContact) {
        setShowLeadForm(true);
        setShowAppointmentPrompt(false);
      }
      
      // Show appointment prompt if AI detects appointment request
      if (data.needsAppointment) {
        setShowAppointmentPrompt(true);
        setShowLeadForm(false);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Fehler",
        description: "Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "Entschuldigung, es gab einen technischen Fehler. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.",
        role: 'assistant',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickResponse = (message: string) => {
    sendMessage(message);
  };

  const handleFeedback = async (messageId: string, wasHelpful: boolean) => {
    if (feedbackGiven.has(messageId)) return;
    
    setFeedbackGiven(prev => new Set(prev).add(messageId));
    
    try {
      // Find the conversation and update it
      const message = messages.find(m => m.id === messageId);
      if (message && message.role === 'assistant') {
        // We'll track this as a new entry with feedback
        await supabase.from('chat_conversations').insert({
          session_id: sessionId,
          user_message: 'FEEDBACK',
          bot_response: message.content.substring(0, 200),
          was_helpful: wasHelpful
        });
      }
      
      toast({
        title: wasHelpful ? "Danke!" : "Danke für Ihr Feedback",
        description: wasHelpful 
          ? "Freut mich, dass ich helfen konnte!" 
          : "Wir werden uns verbessern. Möchten Sie einen Termin vereinbaren?",
      });

      if (!wasHelpful) {
        setShowLeadForm(true);
      }
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
  };

  const submitLeadForm = async () => {
    if (!leadForm.name || !leadForm.email) {
      toast({
        title: "Bitte ausfüllen",
        description: "Name und E-Mail sind erforderlich.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase.from('leads').insert({
        name: leadForm.name,
        email: leadForm.email,
        phone: leadForm.phone || null,
        message: leadForm.message || `Chat-Anfrage vom ${new Date().toLocaleDateString('de-DE')}`,
        service: 'Chat-Support'
      });

      if (error) throw error;

      // Track that lead was captured
      await trackConversation('LEAD_CAPTURED', `Lead: ${leadForm.name}`, 'Lead', true);

      toast({
        title: "Anfrage gesendet!",
        description: "Wir melden uns schnellstmöglich bei Ihnen.",
      });

      setShowLeadForm(false);
      setLeadForm({ name: '', email: '', phone: '', message: '' });

      // Add confirmation message
      const confirmMessage: Message = {
        id: Date.now().toString(),
        content: `Vielen Dank, ${leadForm.name}! Ich habe Ihre Kontaktdaten an unser Team weitergeleitet. Wir melden uns schnellstmöglich bei Ihnen.`,
        role: 'assistant',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, confirmMessage]);

    } catch (error) {
      console.error('Error submitting lead:', error);
      toast({
        title: "Fehler",
        description: "Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    }
  };

  const WHATSAPP_URL = "https://wa.me/4915128958006?text=Hallo,%20ich%20möchte%20gerne%20einen%20Termin%20vereinbaren.";

  const openWhatsApp = async () => {
    // WhatsApp blocks being embedded in iframes (Preview). We try a new tab first.
    const win = window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");

    if (!win) {
      try {
        await navigator.clipboard?.writeText(WHATSAPP_URL);
      } catch {
        // ignore
      }
      toast({
        title: "WhatsApp im Vorschaufenster blockiert",
        description: "Der Link wurde (wenn möglich) in die Zwischenablage kopiert. Bitte in einem neuen Tab öffnen oder im Live-Link testen.",
      });
    }
  };

  const requestAppointment = async () => {
    await trackConversation('APPOINTMENT_REQUEST', 'User clicked appointment button', 'Termin', false, true);
    window.location.href = '/termine';
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-teal-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-cyan-500 to-teal-500 p-2 rounded-lg">
                <Bot className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">KI-Assistant</h1>
                <p className="text-sm text-gray-600">Klartech Müritz IT-Support</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="flex items-center text-sm text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                Online
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Info Banner */}
          <div className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white p-4">
            <div className="flex items-center space-x-2">
              <Sparkles className="h-5 w-5" />
              <span className="font-medium">
                Ich helfe Ihnen bei IT-Problemen! Bei komplexeren Fragen vermittle ich Sie gerne an unser Team.
              </span>
            </div>
          </div>

          {/* Quick Response Buttons */}
          {messages.length <= 1 && (
            <div className="p-4 bg-gray-50 border-b">
              <p className="text-sm text-gray-600 mb-3">Wählen Sie ein Thema oder stellen Sie Ihre Frage:</p>
              <div className="flex flex-wrap gap-2">
                {quickResponses.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickResponse(item.message)}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm hover:bg-cyan-50 hover:border-cyan-300 transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages Area */}
          <div className="h-96 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div key={message.id}>
                <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`flex items-start space-x-2 max-w-xs lg:max-w-md ${
                    message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      message.role === 'user' 
                        ? 'bg-gray-200' 
                        : 'bg-gradient-to-r from-cyan-500 to-teal-500'
                    }`}>
                      {message.role === 'user' ? (
                        <User className="h-4 w-4 text-gray-600" />
                      ) : (
                        <Bot className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <div className={`rounded-lg px-4 py-2 ${
                      message.role === 'user'
                        ? 'bg-cyan-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-900'
                    }`}>
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      <p className={`text-xs mt-1 ${message.role === 'user' ? 'text-cyan-100' : 'text-gray-500'}`}>
                        {formatTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Feedback buttons for assistant messages */}
                {message.role === 'assistant' && message.id !== 'welcome' && !feedbackGiven.has(message.id) && (
                  <div className="flex justify-start ml-10 mt-1 space-x-2">
                    <button
                      onClick={() => handleFeedback(message.id, true)}
                      className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                      title="Hilfreich"
                    >
                      <ThumbsUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleFeedback(message.id, false)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Nicht hilfreich"
                    >
                      <ThumbsDown className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Show contact prompt if AI suggests it */}
                {message.needsContact && !showLeadForm && (
                  <div className="ml-10 mt-2 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <p className="text-sm text-cyan-800 mb-2">Möchten Sie, dass wir Sie kontaktieren?</p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowLeadForm(true)}
                        className="px-3 py-1 bg-cyan-600 text-white text-sm rounded hover:bg-cyan-700"
                      >
                        Ja, bitte
                      </button>
                      <button
                        onClick={requestAppointment}
                        className="px-3 py-1 bg-white border border-cyan-300 text-cyan-700 text-sm rounded hover:bg-cyan-50"
                      >
                        Termin buchen
                      </button>
                    </div>
                  </div>
                )}

                {/* Show appointment options if AI detects appointment request */}
                {message.needsAppointment && !showLeadForm && (
                  <div className="ml-10 mt-2 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-teal-600" />
                      <p className="text-sm text-teal-800 font-medium">Termin vereinbaren</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={openWhatsApp}
                        className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp
                      </button>
                      <a
                        href="tel:+4915128958006"
                        className="flex items-center gap-2 px-3 py-2 bg-teal-600 text-white text-sm rounded hover:bg-teal-700"
                      >
                        <Phone className="h-4 w-4" />
                        Anrufen
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg px-4 py-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-end space-x-2">
              <div className="flex-1">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Beschreiben Sie Ihr IT-Problem..."
                  className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  rows={2}
                  disabled={loading}
                />
              </div>
              <button
                onClick={() => sendMessage()}
                disabled={!inputMessage.trim() || loading}
                className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:shadow-lg text-white p-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={requestAppointment}
            className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow text-left"
          >
            <div className="flex items-center space-x-3">
              <Calendar className="h-8 w-8 text-cyan-600" />
              <div>
                <h3 className="font-medium text-gray-900">Termin buchen</h3>
                <p className="text-sm text-gray-600">Direkter Support-Termin</p>
              </div>
            </div>
          </button>

          <a
            href="tel:+4915128958006"
            className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow"
          >
            <div className="flex items-center space-x-3">
              <Phone className="h-8 w-8 text-green-600" />
              <div>
                <h3 className="font-medium text-gray-900">Anrufen</h3>
                <p className="text-sm text-gray-600">+49 151 28958006</p>
              </div>
            </div>
          </a>

          <a
            href="mailto:kontakt@klartech-mueritz.de"
            className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow"
          >
            <div className="flex items-center space-x-3">
              <Mail className="h-8 w-8 text-blue-600" />
              <div>
                <h3 className="font-medium text-gray-900">E-Mail</h3>
                <p className="text-sm text-gray-600">kontakt@klartech-mueritz.de</p>
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* Lead Capture Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowLeadForm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
            
            <h3 className="text-xl font-bold text-gray-900 mb-2">Wir melden uns bei Ihnen!</h3>
            <p className="text-gray-600 mb-6">
              Hinterlassen Sie Ihre Kontaktdaten und wir kümmern uns um Ihr Anliegen.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Ihr Name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail *
                </label>
                <input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="ihre.email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon (optional)
                </label>
                <input
                  type="tel"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="+49 ..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nachricht (optional)
                </label>
                <textarea
                  value={leadForm.message}
                  onChange={(e) => setLeadForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  rows={3}
                  placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowLeadForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={submitLeadForm}
                className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
              >
                Absenden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
